/**
 * Military vessel layer — WM renders naval traffic as a layer wholly
 * separate from commercial AIS (DeckGLMap.ts:3789-3809): plain red circles,
 * with USNI-derived estimated positions split out in orange with a ring.
 */

import Supercluster from 'supercluster';
import { ScatterplotLayer } from '@deck.gl/layers';
import type { Layer } from '@deck.gl/core';
import type { NormalizedEvent } from '@/sources/base-source';

/** Layer IDs managed by this renderer. */
export const MILITARY_VESSEL_LAYER_IDS = new Set(['naval']);

// ---------------------------------------------------------------------------
// Activity clusters — WM's fleet/sortie concentration discs
// ---------------------------------------------------------------------------

/** A regional concentration of military vessels or flights. */
export interface ActivityCluster {
  lng: number;
  lat: number;
  count: number;
  /** 'exercise' | 'deployment' | 'transit' | 'patrol' | 'transport' | 'unknown' */
  activityType: string;
}

/**
 * Aggregate military position events into regional clusters. WM's clusters
 * come from an upstream feed carrying an explicit activityType; Sentinel
 * derives regions client-side and reads raw.activityType when present —
 * until an activity classifier exists, most clusters are 'unknown' and
 * render in the muted fallback color.
 *
 * No map/reduce: Supercluster's reduce accumulator is a SHALLOW clone of
 * nested props, so a nested counts object gets neighbor counts written
 * into child clusters' stored props. Military subsets are small, so the
 * dominant activity is counted via getLeaves instead.
 */
export function aggregateActivityClusters(events: NormalizedEvent[], zoom: number): ActivityCluster[] {
  const geoEvents = events.filter((e) => e.location);
  if (geoEvents.length < 2) return [];

  const index = new Supercluster<{ activity: string }>({
    radius: 60,
    maxZoom: 10,
    minPoints: 2,
  });

  index.load(geoEvents.map((e) => {
    const raw = e.raw as Record<string, unknown> | undefined;
    return {
      type: 'Feature' as const,
      geometry: { type: 'Point' as const, coordinates: [e.location!.lng, e.location!.lat] },
      properties: {
        activity: typeof raw?.activityType === 'string' ? raw.activityType : 'unknown',
      },
    };
  }));

  const clusters: ActivityCluster[] = [];
  for (const f of index.getClusters([-180, -85, 180, 85], Math.floor(zoom))) {
    const props = f.properties as Record<string, unknown>;
    if (!props.cluster) continue;

    const activityCounts: Record<string, number> = {};
    for (const leaf of index.getLeaves(props.cluster_id as number, Infinity)) {
      const activity = (leaf.properties as { activity: string }).activity;
      activityCounts[activity] = (activityCounts[activity] ?? 0) + 1;
    }

    let dominant = 'unknown';
    let dominantCount = -1;
    for (const [key, value] of Object.entries(activityCounts)) {
      if (value > dominantCount) {
        dominant = key;
        dominantCount = value;
      }
    }

    clusters.push({
      lng: f.geometry.coordinates[0],
      lat: f.geometry.coordinates[1],
      count: props.point_count as number,
      activityType: dominant,
    });
  }
  return clusters;
}

/**
 * Fleet concentration discs — RED family, deliberately hue-separated from
 * the blue flight clusters (WM DeckGLMap.ts:3811-3828).
 */
export function createMilitaryVesselClusterLayers(events: NormalizedEvent[], zoom: number): Layer[] {
  const clusters = aggregateActivityClusters(events, zoom);
  if (clusters.length === 0) return [];

  return [new ScatterplotLayer<ActivityCluster>({
    id: 'military-vessel-clusters-layer',
    data: clusters,
    pickable: true,
    filled: true,
    radiusUnits: 'meters' as const,
    getPosition: (d) => [d.lng, d.lat],
    getRadius: (d) => 15_000 + (d.count || 1) * 3000,
    getFillColor: (d) => {
      if (d.activityType === 'exercise' || d.activityType === 'deployment') {
        return [255, 100, 100, 200] as [number, number, number, number];
      }
      if (d.activityType === 'transit') {
        return [255, 180, 100, 180] as [number, number, number, number];
      }
      return [200, 150, 150, 160] as [number, number, number, number];
    },
    radiusMinPixels: 8,
    radiusMaxPixels: 25,
  })];
}

function isUsniEstimate(d: NormalizedEvent): boolean {
  const raw = d.raw as Record<string, unknown> | undefined;
  return Boolean(raw?.usniSource);
}

/** Create the military vessel layer (WM DeckGLMap.ts:3789-3809, verbatim). */
export function createMilitaryVesselLayers(events: NormalizedEvent[]): Layer[] {
  if (events.length === 0) return [];

  return [new ScatterplotLayer<NormalizedEvent>({
    id: 'military-vessels-layer',
    data: events,
    pickable: true,
    filled: true,
    stroked: true,
    radiusUnits: 'meters' as const,
    getPosition: (d) => d.location ? [d.location.lng, d.location.lat] : [0, 0],
    getRadius: 6000,
    // Live-AIS warship = solid red; USNI-derived estimate = dimmer orange
    getFillColor: (d) => isUsniEstimate(d)
      ? [255, 160, 60, 160] as [number, number, number, number]
      : [255, 100, 100, 220] as [number, number, number, number],
    // Estimates get a visible orange ring; live positions none
    getLineColor: (d) => isUsniEstimate(d)
      ? [255, 180, 80, 200] as [number, number, number, number]
      : [0, 0, 0, 0] as [number, number, number, number],
    lineWidthMinPixels: 2,
    radiusMinPixels: 4,
    radiusMaxPixels: 10,
  })];
}
